import { NextRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';

import { NO_DATA_PLACEHOLDER } from '../../constants/labels';
import { Address } from '../../apolloClient/types';
import { ATT } from '../constants/constants';
import { LOGIN_AS_USER } from '../constants/authConstants';
import { translate } from 'constants/listings/statusCodes';
import {
  HistoryItem,
  Listing,
  ListingPropertyType,
} from 'apolloClient/types/Units';
import { getClosedStatusLabel } from 'lib/getClosedStatusLabel';
import { format, isValid } from 'date-fns';

export const emailRegex =
  /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;

export const checkNan = (toCheck: any) => !Number.isNaN(Number(toCheck));

export const centenaryNumberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
}).format;

export const numberFormatter = new Intl.NumberFormat('en-US', {}).format;

export function formatNumberOrRangeSafe(input: string): string {
  try {
    const trimmed = input.trim();

    if (!trimmed) {
      return input;
    }

    // Range case: "123-1244"
    if (trimmed.includes('-')) {
      const parts = trimmed.split('-');

      // Only support simple ranges
      if (parts.length !== 2) {
        return input;
      }

      const [start, end] = parts.map((p) => p.trim());

      // Validate both sides are numeric
      if (!/^\d+$/.test(start) || !/^\d+$/.test(end)) {
        return input;
      }

      return `${numberFormatter(Number(start))}-${numberFormatter(
        Number(end)
      )}`;
    }

    // Single number case
    if (/^\d+$/.test(trimmed)) {
      return numberFormatter(Number(trimmed));
    }

    return input;
  } catch {
    // Absolute safety fallback
    return input;
  }
}

export const sqftFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format;

export const sqftFormatterM2 = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format;

export const priceFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format;

export const priceFormatterWithCent = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format;

export const decimalNumberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
}).format;

export const decimalNumberFormatterWithoutZero = new Intl.NumberFormat(
  'en-US',
  {
    maximumFractionDigits: 1,
  }
).format;

export const decimalPriceFormatterWithoutZero = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
}).format;

export const percentFormatterFractionZero = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
}).format;

export const abbreviatedPriceFormatterWithPrecision = (precision: number = 2) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: precision,
    notation: 'compact',
    compactDisplay: 'short',
  }).format;

export const abbreviatedPriceFormatter =
  abbreviatedPriceFormatterWithPrecision(0);

export const abbreviatedPriceFormatterGain =
  abbreviatedPriceFormatterWithPrecision(1);

export const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
}).format;

export const isNotEmpty = (value: string | undefined) => {
  return value !== undefined && value !== '';
};

export const validateEmail = (value: string | undefined) => {
  return isNotEmpty(value) && emailRegex.test(value as string);
};

export const removeQueryParams = (params: string[], router: NextRouter) => {
  const { pathname, query } = router;
  const paramsObj = new URLSearchParams(query as {});
  for (const param of params) {
    paramsObj.delete(param);
  }
  router.replace({ pathname, query: paramsObj.toString() }, undefined, {
    shallow: true,
  });
};

export const getAgentIdFromLocalStorage = (): string | null => {
  return localStorage.getItem('agent');
};

// "Log in as user" tabs keep the FUB identity in sessionStorage so the client's
// lead never reaches the admin's localStorage. Not isLoginAsUser(): circular import.
export const getLeadStorage = (): Storage =>
  window.sessionStorage.getItem(LOGIN_AS_USER) === 'true'
    ? window.sessionStorage
    : window.localStorage;

export const getLeadIdFromLocalStorage = (): string | null => {
  const value = getLeadStorage().getItem('leadId');
  return value && value !== 'undefined' ? value : null;
};

export const getbrowserUUIDFromLocalStorage = (): string | null => {
  return getLeadStorage().getItem('browserUUID');
};

/**
 * Reads the visitor's browser UUID, minting one if it is missing.
 *
 * The id is normally created by the activity tracker on the first page view
 * (_app.tsx), but that path returns early during admin impersonation and may
 * simply not have run yet when someone signs in. A missing id is not something
 * the user should pay for — `moveActivitiesToUser` requires it (`String!`) and
 * the id is also stored on the account for Follow Up Boss — so mint one rather
 * than hand back null.
 *
 * Still nullable: with storage blocked (private mode, disabled storage) there is
 * nowhere to persist it, and inventing a throwaway id per call would be worse
 * than none — it would key activity to something that never survives.
 */
export const ensureBrowserUUID = (): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    const storage = getLeadStorage();
    const existing = storage.getItem('browserUUID');

    if (existing) return existing;

    const uuid = uuidv4();
    storage.setItem('browserUUID', uuid);

    return uuid;
  } catch {
    return null;
  }
};

export const saveLeadIdToLocalStorage = (leadId: string) => {
  if (leadId && leadId !== 'undefined') {
    getLeadStorage().setItem('leadId', leadId);
  }
};

// agentsData (incl. isAgentSep) is refreshed at most once a day so a change on
// the backend propagates without spamming the FUB-backed resolver on every load.
const AGENTS_DATA_TTL_MS = 24 * 60 * 60 * 1000; // 1 day
const AGENTS_DATA_UPDATED_AT_KEY = 'agentsDataUpdatedAt';

export const saveAgentDataToLocalStorage = (agentsData: string) => {
  localStorage.setItem('agentsData', agentsData);
  localStorage.setItem(AGENTS_DATA_UPDATED_AT_KEY, String(Date.now()));
};

export const getAgentDataFromLocalStorage = (): string | null => {
  return localStorage.getItem('agentsData');
};

// True when agentsData has never been stamped or its stamp is older than the TTL.
// Callers use it to force a refresh of the cached agentsData.
export const isAgentDataExpired = (): boolean => {
  const raw = localStorage.getItem(AGENTS_DATA_UPDATED_AT_KEY);
  const updatedAt = raw ? Number(raw) : NaN;

  if (!Number.isFinite(updatedAt)) {
    return true;
  }

  return Date.now() - updatedAt > AGENTS_DATA_TTL_MS;
};

export const saveAgentIdToLocalStorage = (id: string) => {
  const idFromLocalStorage = getAgentIdFromLocalStorage();
  (idFromLocalStorage === null || idFromLocalStorage !== id) &&
    localStorage.setItem('agent', id);
};

export const getNumberFromString = (string: string) => {
  return Number(string.replace(/[^0-9.]/g, ''));
};

export const normilizeUnitDescription = (field: string) => {
  return field?.trim().split(',').join(', ');
};

export const isEqualValues = (valueA: any, valueB: any): boolean => {
  return JSON.stringify(valueA) === JSON.stringify(valueB);
};

export function setCookie(name: string, value: string, expDays: number) {
  let expires = '';
  let domain = '';
  if (expDays) {
    const date = new Date();
    date.setTime(date.getTime() + expDays * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  if (location.hostname.includes('condoblackbook.com')) {
    domain = '; Domain=condoblackbook.com';
  }
  document.cookie = name + '=' + (value || '') + expires + domain + '; path=/';
}

export function getCookie(name: string, cookies?: string): string {
  let res;
  if (typeof window !== 'undefined' || cookies) {
    const normalizedName = name + '=';
    const normalizeCookies = cookies || decodeURIComponent(document.cookie);
    const cookiesArr = normalizeCookies.split('; ');
    cookiesArr.forEach((val) => {
      if (val.indexOf(normalizedName) === 0)
        res = val.substring(normalizedName.length);
    });
  }
  return res || '';
}

export function setLoggedAsUserSessionStorage(name: string, value: string) {
  window.sessionStorage.setItem(name, value);
  window.sessionStorage.setItem('loggedAsUser', 'true');
}

export function getLoggedAsUserSessionStorage() {
  let res;
  if (
    typeof window !== 'undefined' &&
    window.sessionStorage.getItem('authData') !== null
  ) {
    res = window.sessionStorage.getItem('authData')!;
  }
  return res || '';
}

const ASSOCIATION_FEE_PERIOD_LABELS: Record<string, string> = {
  Monthly: 'monthly',
  Quarterly: 'quarterly',
  Annually: 'yearly',
  Yearly: 'yearly',
  Semi: 'semi-annually',
  'Semi-Annually': 'semi-annually',
  Semi_Annually: 'semi-annually',
};

export function getAssociationFeePeriodLabel(
  associationFeePaidPer?: string | null
): string {
  return (
    (associationFeePaidPer &&
      ASSOCIATION_FEE_PERIOD_LABELS[associationFeePaidPer]) ||
    'monthly'
  );
}

export function getAssociationFee(values: {
  hoaFees: number;
  maintenanceFee: number;
  associationFeePaidPer?: string | null;
}): string {
  const { hoaFees, maintenanceFee, associationFeePaidPer } = values;
  const fee =
    (hoaFees > 0 && hoaFees) || (maintenanceFee > 0 && maintenanceFee);

  if (!fee) return NO_DATA_PLACEHOLDER;
  return `${priceFormatter(fee)}/${getAssociationFeePeriodLabel(
    associationFeePaidPer
  )}`;
}

export function getNormalizedAddress(data: Address & { city?: string }) {
  const { streetNumber, streetName, streetType, zipCode, city } = data;
  let baseAddress = `${streetNumber} ${streetName} ${streetType}`;
  if (city) baseAddress += `, ${city}`;
  baseAddress += `, ${zipCode}`;
  return baseAddress;
}

export function switchWasOnceLogged() {
  !wasOnceLogged() && localStorage.setItem('userWasLogged', 'true');
}

export function wasOnceLogged(): boolean {
  return Boolean(localStorage.getItem('userWasLogged'));
}

export function getATTFromLocalStorage(): string | null {
  return localStorage.getItem(ATT);
}

interface Data {
  previousStatusCode: string;
  statusCode: string;
  propertyTypeCode: string;
}

type StatusCode = keyof typeof translate;

export const getHistoryPropertyTypeCode = (
  history: Pick<HistoryItem, 'mlsListingId'>,
  listings: ListingPropertyType[],
  fallback: string
): string =>
  (history.mlsListingId &&
    listings.find((item) => item.mlsListingId === history.mlsListingId)
      ?.propertyTypeCode) ||
  fallback;

// Price and % change of a price history row. A removed price (null/0) is N/A,
// not "$NaN -100 %".
export const getHistoryPrice = (
  history: Pick<HistoryItem, 'oldPrice' | 'newPrice'>
): { price: string; delta: number | string } => {
  const { oldPrice, newPrice } = history;

  if (!(newPrice > 0)) {
    return { price: NO_DATA_PLACEHOLDER, delta: NO_DATA_PLACEHOLDER };
  }

  if (!(oldPrice > 0)) {
    return { price: priceFormatter(newPrice), delta: NO_DATA_PLACEHOLDER };
  }

  const delta = +centenaryNumberFormatter(
    ((newPrice - oldPrice) * 100.0) / oldPrice
  );

  return {
    price: priceFormatter(newPrice),
    delta: delta || NO_DATA_PLACEHOLDER,
  };
};

export const getStatusText = (data: Data): string => {
  const { previousStatusCode, statusCode, propertyTypeCode } = data;

  if (statusCode === previousStatusCode) {
    return 'Price change';
  }

  const statusMapping: Record<string, string> = {
    AC: 'Accepting Backups',
    CSL: 'Coming Soon',
    B: 'Under Contract/Backup',
  };

  if (statusCode === 'CS' || statusCode === 'R') {
    return getClosedStatusLabel(propertyTypeCode, statusCode);
  }

  if (statusMapping[statusCode]) {
    return statusMapping[statusCode];
  }

  if (
    previousStatusCode !== 'AC' &&
    previousStatusCode !== 'A' &&
    (statusCode === 'AC' || statusCode === 'A')
  ) {
    return propertyTypeCode === 'R' ? 'Listed for rent' : 'Listed for sale';
  }

  if (
    previousStatusCode !== 'C' &&
    previousStatusCode !== 'X' &&
    previousStatusCode !== 'PS' &&
    previousStatusCode !== 'T' &&
    previousStatusCode !== 'W' &&
    previousStatusCode !== 'Q' &&
    statusCode === 'PS'
  ) {
    return 'Under contract';
  }

  if (
    previousStatusCode !== 'C' &&
    previousStatusCode !== 'X' &&
    previousStatusCode !== 'PS' &&
    previousStatusCode !== 'T' &&
    previousStatusCode !== 'W' &&
    previousStatusCode !== 'Q' &&
    statusCode !== 'AC' &&
    statusCode !== 'A' &&
    statusCode !== 'CS' &&
    statusCode !== 'R'
  ) {
    return 'Listing removed';
  }

  if (previousStatusCode === 'X' && statusCode === 'Q') {
    return 'Status change Expired -> Terminated';
  }

  return `Status change "${translate[previousStatusCode as StatusCode]}" -> "${
    translate[statusCode as StatusCode]
  }"`;
};

export const getFormattedPriceChangeInDollars = (
  price: number,
  previousPrice: number
): string => {
  const priceChange = price - previousPrice;
  const absChange = Math.abs(priceChange);
  const sign = priceChange > 0 ? '+' : '-';

  if (absChange < 10000) {
    return `${sign}$${numberFormatter(absChange)}`;
  }

  if (absChange < 1000000) {
    if (absChange >= 100000) return `${sign}$${Math.round(absChange / 1000)}K`;
    const valueInK = Math.round(absChange / 100) / 10;
    const formattedK =
      valueInK % 1 === 0 ? Math.floor(valueInK) : valueInK.toFixed(1);
    return `${sign}$${formattedK}K`;
  }

  const valueInM = absChange / 1000000;

  if (valueInM >= 10) {
    const roundedValue = Math.round(valueInM * 10) / 10;
    return `${sign}$${roundedValue.toFixed(1).replace(/\.0$/, '')}M`;
  }

  const roundedValue = Math.round((valueInM + Number.EPSILON) * 100) / 100;
  return `${sign}$${roundedValue.toFixed(2).replace(/\.?0+$/, '')}M`;
};

export const getAmericanDate = (dateString: string) => {
  return isValid(new Date(dateString))
    ? format(new Date(dateString), 'MM/dd/yyyy')
    : 'N/A';
};

export const scrollToElement = (
  element: HTMLElement | null,
  isHide: boolean,
  options: ScrollIntoViewOptions = {
    behavior: 'instant' as ScrollBehavior,
    block: 'start',
  }
) => {
  if (!element || !isHide) return;
  element.scrollIntoView(options);
};

export const pageBuilderValidator = (description: any) => {
  if (!description || description?.ROOT?.nodes?.length === 0)
    return {
      isDescriptionValid: false,
      validElementsCount: 0,
      cleanedDescription: description,
    };

  const isNodeEmpty = (node: any) => {
    if (!node) return true;

    const isEmptyEditor =
      node?.type?.resolvedName === 'Editor' &&
      (!node?.props?.text || node?.props?.text.trim() === '');

    const isEmptyImage =
      node?.type?.resolvedName === 'Image' &&
      (!node?.props?.image || node?.props?.image.length === 0);

    const isEmptyCarousel =
      node?.type?.resolvedName === 'Carousel' &&
      (!node?.props?.images || node?.props?.images?.length === 0);

    return isEmptyEditor || isEmptyImage || isEmptyCarousel;
  };

  // Create a cleaned copy of the description
  const cleanedDescription = { ...description };
  const rootNodeIds = description.ROOT?.nodes || [];

  // Filter out empty nodes
  const validNodeIds: string[] = [];
  const nodesToKeep: Record<string, any> = { ROOT: { ...description.ROOT } };

  // Recurse into a node's own .nodes so nested children (e.g. inside a Container) are kept too
  const collectNode = (nodeId: string): boolean => {
    const node = description[nodeId];
    if (isNodeEmpty(node)) return false;

    const nodeCopy = { ...node };
    if (node?.isCanvas) {
      const childIds = Array.isArray(node?.nodes) ? node.nodes : [];
      nodeCopy.nodes = childIds.filter((childId: string) =>
        collectNode(childId)
      );
      if (nodeCopy.nodes.length === 0) return false;
    }
    nodesToKeep[nodeId] = nodeCopy;
    return true;
  };

  rootNodeIds.forEach((nodeId: string) => {
    if (collectNode(nodeId)) {
      validNodeIds.push(nodeId);
    }
  });

  // Update ROOT.nodes to only include valid nodes
  nodesToKeep.ROOT.nodes = validNodeIds;

  const validElementsCount = validNodeIds.length;

  // if all nodes are empty, return false
  if (validElementsCount === 0)
    return {
      isDescriptionValid: false,
      validElementsCount: 0,
      cleanedDescription: description,
    };

  return {
    isDescriptionValid: true,
    validElementsCount,
    cleanedDescription: nodesToKeep,
  };
};

export function getListingUnitIdentifier(
  listing: Listing | undefined | null
): string {
  return listing?.unitNumber || listing?.unit?.data?.attributes?.unique || '';
}

export const contactListingAddress = (
  address: Address | undefined,
  city: string
) => {
  const { streetDirection, streetName, streetNumber, streetType, zipCode } =
    address || {};
  const shortStreetDirection = (streetDirection?.match(/[A-Z]/g) || []).join(
    ''
  );
  const streetTypeToDisplay =
    streetType && streetType !== 'Unknown' ? streetType : '';

  const streetParts = [
    streetNumber,
    shortStreetDirection,
    streetName,
    streetTypeToDisplay,
  ]
    .filter(Boolean)
    .join(' ');
  const listingAddress = [
    streetParts || undefined,
    city,
    zipCode ? `FL ${zipCode}` : undefined,
  ]
    .filter(Boolean)
    .join(', ');
  return listingAddress;
};
